/** biome-ignore-all lint/suspicious/noExplicitAny: it's ok */
/** biome-ignore-all lint/complexity/noStaticOnlyClass: it's ok */
import ArticleUpdaterService from "@components/Article/ArticleUpdater/ArticleUpdaterService";
import { createEventEmitter, type Event } from "@core/Event/EventEmitter";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import FetchService from "@core-ui/ApiServices/FetchService";
import DefaultError from "@ext/errorHandlers/logic/DefaultError";
import tryOpenMergeConflict from "@ext/git/actions/MergeConflictHandler/logic/tryOpenMergeConflict";
import type ClientSyncResult from "@ext/git/core/model/ClientSyncResult";

export type SyncServiceEvents = Event<"start"> &
	Event<"finish", { syncData: ClientSyncResult }> &
	Event<"error", { error: DefaultError; apiUrlCreator: ApiUrlCreator }>;

type SyncResult = { resOk: true; syncData: ClientSyncResult } | { resOk: false; error: DefaultError };

export default class SyncService {
	private static _events = createEventEmitter<SyncServiceEvents>();

	static get events() {
		return SyncService._events;
	}

	public static async sync(apiUrlCreator: ApiUrlCreator) {
		await SyncService.events.emit("start", {});
		const data = await SyncService._sync(apiUrlCreator);

		if (!data.resOk && "error" in data) {
			await SyncService.events.emit("error", {
				error: data.error,
				apiUrlCreator,
			});
			return;
		}

		await SyncService._onFinish(data.syncData, apiUrlCreator);
		await SyncService.events.emit("finish", { syncData: data.syncData });
	}

	private static async _sync(apiUrlCreator: ApiUrlCreator): Promise<SyncResult> {
		const res = await FetchService.fetch<ClientSyncResult>(apiUrlCreator.getStorageSyncUrl());
		if (!res.ok) {
			const errorFromFetch = ((res as any).error ?? (res as any).body) as DefaultError;
			if (errorFromFetch) return { resOk: false, error: errorFromFetch };

			try {
				return { resOk: false, error: (await res.json()) as unknown as DefaultError };
			} catch {
				return { resOk: false, error: new DefaultError("Sync failed") };
			}
		}
		return { resOk: true, syncData: await res.json() };
	}

	private static async _onFinish(syncData: ClientSyncResult, apiUrlCreator: ApiUrlCreator) {
		if (!syncData.mergeData.ok) {
			tryOpenMergeConflict({ mergeData: { ...syncData.mergeData } });
			return;
		}
		await ArticleUpdaterService.update(apiUrlCreator);
		void refreshPage();
	}
}
